/**
 * Destino declarado no AIS → nome legível — funções puras (testadas em
 * `destinos.test.ts`).
 *
 * O campo `Destination` é texto livre de 20 caracteres digitado na ponte. Não
 * há norma: o mesmo porto aparece como `BR VDC`, `BRVDC`, `VILA DO CONDE`,
 * `>BRVDC` e `BRVDC><`. Medido na Amazônia em 20/09/2026: "BR VDC" e "BRVDC"
 * no mesmo ciclo, em navios diferentes.
 *
 * Aqui o texto é limpo e, quando reconhecemos um UN/LOCODE, ganha o nome por
 * extenso. O que não for reconhecido é mostrado **como veio** — inventar um
 * destino é pior que exibir uma sigla.
 *
 * E o rótulo na interface é sempre "destino DECLARADO": este campo é o que a
 * embarcação diz, não o que ela faz.
 */

/**
 * UN/LOCODE dos portos que aparecem no corredor amazônico e nos destinos mais
 * comuns da frota que passa por aqui. Chave sem o espaço do país.
 */
const PORTOS: Readonly<Record<string, string>> = {
  // ---- Amazônia Legal e arco norte ----
  BRMAO: 'Manaus (AM)',
  BRBEL: 'Belém (PA)',
  BRSTM: 'Santarém (PA)',
  BRVDC: 'Vila do Conde (Barcarena, PA)',
  BRIQI: 'Itaqui (São Luís, MA)',
  BRMCP: 'Macapá (AP)',
  BRSNM: 'Santana (AP)',
  BRPVH: 'Porto Velho (RO)',
  BRTBT: 'Tabatinga (AM)',

  // ---- Outros portos brasileiros ----
  BRSSZ: 'Santos (SP)',
  BRPNG: 'Paranaguá (PR)',
  BRRIG: 'Rio Grande (RS)',
  BRVIX: 'Vitória (ES)',
  BRRIO: 'Rio de Janeiro (RJ)',
  BRSUA: 'Suape (PE)',
  BRSSA: 'Salvador (BA)',
  BRFOR: 'Fortaleza (CE)',
  BRIBB: 'Imbituba (SC)',
  BRITJ: 'Itajaí (SC)',
  BRSFS: 'São Francisco do Sul (SC)',
  BRIOA: 'Itaguaí (RJ)',
  BRPEC: 'Pecém (CE)',
  BRARB: 'Aratu (BA)',
  BRMCZ: 'Maceió (AL)',
  BRNAT: 'Natal (RN)',
  BRREC: 'Recife (PE)',

  // ---- Destinos internacionais frequentes do corredor da soja ----
  CNSHA: 'Xangai (China)',
  CNQIN: 'Qingdao (China)',
  CNNBO: 'Ningbo (China)',
  NLRTM: 'Roterdã (Países Baixos)',
  ESALG: 'Algeciras (Espanha)',
  MAPTM: 'Tanger Med (Marrocos)',
  PACTB: 'Cristóbal (Panamá)',
  PABLB: 'Balboa (Panamá)',
  USNYC: 'Nova York (EUA)',
  USHOU: 'Houston (EUA)',
  USNOL: 'Nova Orleans (EUA)',
  ARBUE: 'Buenos Aires (Argentina)',
  ARROS: 'Rosário (Argentina)',
  UYMVD: 'Montevidéu (Uruguai)',
  COCTG: 'Cartagena (Colômbia)',
  PECLL: 'Callao (Peru)',
  PEIQT: 'Iquitos (Peru)',
  SRPBM: 'Paramaribo (Suriname)',
  GYGEO: 'Georgetown (Guiana)',
  TTPOS: 'Port of Spain (Trinidad e Tobago)',
};

/** Marcadores que o pessoal da ponte usa como enfeite e não são destino. */
const LIXO = /^[<>\-.\s*/\\]+|[<>\-.\s*/\\]+$/g;

/**
 * Muita gente escreve a viagem inteira: `BRPEC>BRMAO` (medido em 20/09/2026,
 * no NC BRUMA). O destino é o último trecho; o resto é de onde veio.
 */
function partirRota(limpo: string): { origem: string | null; destino: string } {
  const partes = limpo
    .split(/\s*>+\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (partes.length < 2) return { origem: null, destino: limpo };
  return { origem: partes[0]!, destino: partes[partes.length - 1]! };
}

/**
 * Limpa o texto cru: tira enfeite, colapsa espaço e normaliza maiúsculas.
 * Devolve `null` quando não sobra nada — e não sobrar nada é comum: metade da
 * frota regional transmite o campo em branco.
 */
export function limparDestino(cru: string | null | undefined): string | null {
  if (typeof cru !== 'string') return null;
  const limpo = cru.replace(/\u0000/g, '').replace(LIXO, '').replace(/\s+/g, ' ').trim();
  if (limpo.length === 0) return null;
  // "0", "N/A", "NIL", "NONE", "UNKNOWN" são jeitos de dizer "não informado".
  if (/^(0+|n\/?a|nil|none|unknown|no dest\w*)$/i.test(limpo)) return null;
  return limpo;
}

/**
 * Tenta ler o texto como UN/LOCODE. Aceita `BRVDC`, `BR VDC` e `BR-VDC`;
 * exige 2 letras de país + 3 de local, que é a forma da norma.
 */
export function comoLocode(limpo: string): string | null {
  const junto = limpo.toUpperCase().replace(/[\s-]/g, '');
  return /^[A-Z]{2}[A-Z0-9]{3}$/.test(junto) ? junto : null;
}

export interface Destino {
  /** O que mostrar em destaque. */
  rotulo: string;
  /** O código original, quando o rótulo é um nome traduzido; senão `null`. */
  codigo: string | null;
  /** `true` quando reconhecemos o porto — a interface pode destacar. */
  conhecido: boolean;
  /** De onde veio, quando a embarcação declarou a viagem inteira. */
  origem: string | null;
}

/** Resolve um trecho isolado: LOCODE, sigla de 3 letras ou nome por extenso. */
function resolverTrecho(texto: string): Omit<Destino, 'origem'> {
  const locode = comoLocode(texto);
  if (locode) {
    const nome = PORTOS[locode];
    if (nome) return { rotulo: nome, codigo: locode, conhecido: true };
    return { rotulo: locode, codigo: null, conhecido: false };
  }

  // Sigla de três letras, sem o país: `MAO`, `BEL`, `STM`. Medido no OC
  // VALENTE (`MAO`). Só vale quando `BR` + a sigla é um porto que
  // conhecemos — assim não inventamos destino a partir de três letras
  // quaisquer.
  const curto = texto.toUpperCase().replace(/[\s-]/g, '');
  if (/^[A-Z]{3}$/.test(curto)) {
    const comPais = `BR${curto}`;
    const nome = PORTOS[comPais];
    if (nome) return { rotulo: nome, codigo: comPais, conhecido: true };
  }

  return { rotulo: capitalizar(texto), codigo: null, conhecido: false };
}

/**
 * Destino pronto para a tela. Texto não reconhecido volta como veio (só
 * limpo): o AmaView mostra o que a fonte diz, não o que ele gostaria que ela
 * dissesse.
 */
export function destinoDe(cru: string | null | undefined): Destino | null {
  const limpo = limparDestino(cru);
  if (limpo === null) return null;

  const { origem, destino } = partirRota(limpo);
  const r = resolverTrecho(destino);
  return { ...r, origem: origem === null ? null : resolverTrecho(origem).rotulo };
}

/**
 * `VILA DO CONDE` → `Vila do Conde`; `MANAUS-AM` → `Manaus-AM`.
 *
 * Siglas de até 3 letras em caixa alta ficam como estão: `AM`, `PA` e `BR` são
 * códigos, não palavras a capitalizar.
 */
function capitalizar(s: string): string {
  if (/[a-z]/.test(s)) return s;
  const menores = new Set(['de', 'do', 'da', 'dos', 'das', 'e']);
  return s
    .split(/(\s+|-)/)
    .map((p, i) => {
      if (/^(\s+|-)$/.test(p) || p === '') return p;
      const baixo = p.toLowerCase();
      // As preposições vêm ANTES da regra de siglas: `DO` tem duas letras e
      // em caixa alta parece código, mas em `VILA DO CONDE` é preposição.
      if (i > 0 && menores.has(baixo)) return baixo;
      if (p.length <= 3 && /^[A-Z]+$/.test(p)) return p;
      return baixo[0]!.toUpperCase() + baixo.slice(1);
    })
    .join('');
}

/** Só para os testes e para o painel contar portos reconhecidos. */
export function portosConhecidos(): readonly string[] {
  return Object.keys(PORTOS);
}
